import React from 'react';
import ArrowLeftIcon from './icons/ArrowLeftIcon';

interface LegalDocumentPageProps {
  kind: 'privacy' | 'terms' | 'support' | 'marketing' | 'eula';
  onClose: () => void;
}

const effectiveDate = 'April 21, 2026';

const PrivacyContent = () => (
  <div className="space-y-6 text-sm leading-7 text-gray-300">
    <p>
      This Privacy Policy explains how RIDAZ collects, uses, stores, and shares information when you use the RIDAZ app, website, and related services. RIDAZ is a social platform for athletes and creators to share posts, Zips, map spots, messages, and profile information.
    </p>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Information We Collect</h2>
      <p>We collect information you provide directly, including your name, username, email address, profile photo, cover photo, posts, comments, messages, ratings, spots, uploaded media, and any profile details you choose to add.</p>
      <p>When you sign in with Google, RIDAZ may receive your Google account name, email address, and profile image. We use that information only to create, secure, and operate your RIDAZ account and user-facing features.</p>
      <p>We may also collect technical and usage information such as device/browser details, IP-based diagnostics, app interactions, and approximate timestamps of account activity. If you choose to use map or live-location features, we collect location data that you actively submit or share.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">How We Use Information</h2>
      <p>We use your information to provide and improve RIDAZ features, including account creation, authentication, profile setup, feed and messaging functionality, post uploads, map spots, ratings, moderation, safety monitoring, and customer support.</p>
      <p>Google user data is used only to authenticate you, populate your RIDAZ profile, and support the features you request inside the app. We do not use Google user data for advertising or sale to third parties.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">How We Share Information</h2>
      <p>Your profile, posts, comments, ratings, map spots, and other content may be visible to other RIDAZ users depending on how the feature works. We may share information with service providers that host infrastructure, authentication, storage, analytics, or support systems for RIDAZ.</p>
      <p>We may also disclose information if required by law, to enforce our terms, to investigate abuse, or to protect the rights, safety, and security of RIDAZ, our users, or the public.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Data Storage and Security</h2>
      <p>RIDAZ uses technical and organizational measures intended to protect data, including authenticated access controls, hosted cloud infrastructure, and transport-layer protections where supported. No system is perfectly secure, and we cannot guarantee absolute security.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Data Retention</h2>
      <p>We retain information for as long as needed to operate RIDAZ, comply with legal obligations, resolve disputes, and enforce agreements. Some content may persist in backups, logs, or cached systems for a limited period after deletion.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Your Choices</h2>
      <p>You can update profile information inside the app settings. If you want your account or data removed, contact RIDAZ support at <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a>.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Children</h2>
      <p>RIDAZ is not intended for children under 13. If you believe a child under 13 has provided personal information, contact us so we can review and remove it.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Changes</h2>
      <p>We may update this Privacy Policy from time to time. If we make material changes, we may update the effective date and provide notice inside RIDAZ where appropriate.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Contact</h2>
      <p>For privacy questions, contact <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a>.</p>
    </section>
  </div>
);

const TermsContent = () => (
  <div className="space-y-6 text-sm leading-7 text-gray-300">
    <p>
      These Terms of Service govern your use of RIDAZ, including the RIDAZ website, mobile experience, and related services. By using RIDAZ, you agree to these terms.
    </p>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Use of RIDAZ</h2>
      <p>You must use RIDAZ lawfully and responsibly. You may not misuse the service, interfere with its operation, impersonate others, scrape data without permission, attempt unauthorized access, or upload illegal, infringing, or abusive content.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Accounts</h2>
      <p>You are responsible for your account, your login methods, and the activity that occurs under your account. Information you provide must be accurate and not misleading.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">User Content</h2>
      <p>You retain ownership of content you submit to RIDAZ, but you grant RIDAZ a non-exclusive license to host, store, reproduce, display, and distribute that content as needed to operate and improve the service.</p>
      <p>You are responsible for ensuring you have the rights to the content you upload, including photos, videos, usernames, comments, and map-related content.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Moderation and Enforcement</h2>
      <p>RIDAZ may remove content, limit accounts, or suspend access if we believe content or conduct violates these terms, creates safety risk, or exposes RIDAZ or its users to harm.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Automated and AI-Assisted Features</h2>
      <p>RIDAZ may use automated systems, including artificial intelligence and machine-learning tools, to support features such as content moderation, spam and abuse detection, personalized recommendations, and other in-app functionality. Outputs from these systems are provided on an assistive basis and may be inaccurate or incomplete. In accordance with Federal Trade Commission guidance and other applicable law, RIDAZ will identify AI-generated or AI-assisted content where required and will not misrepresent the involvement of automated or AI systems in RIDAZ features.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Location, Spots, and Safety</h2>
      <p>Spot information, ratings, and user-submitted map content are provided by users and may be inaccurate. You are solely responsible for how you use location information, and you assume all risk related to visiting spots, sharing location, or participating in activities discussed on RIDAZ.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Termination</h2>
      <p>You may stop using RIDAZ at any time. RIDAZ may suspend or terminate access if necessary to protect the platform, enforce these terms, or comply with legal requirements.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Disclaimers</h2>
      <p>RIDAZ is provided on an “as is” and “as available” basis without warranties of any kind to the fullest extent allowed by law. We do not guarantee uninterrupted availability, complete accuracy, or error-free operation.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Limitation of Liability</h2>
      <p>To the fullest extent permitted by law, RIDAZ and its operators are not liable for indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the service.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Binding Arbitration and Class Action Waiver</h2>
      <p>You and RIDAZ agree that any dispute, claim, or controversy arising out of or relating to these Terms or your use of RIDAZ will be resolved through binding, individual arbitration rather than in court, except that either party may bring an individual claim in small claims court if it qualifies.</p>
      <p>Arbitration will be administered by the American Arbitration Association under its Consumer Arbitration Rules, and judgment on the arbitration award may be entered in any court with jurisdiction. YOU AND RIDAZ EACH WAIVE THE RIGHT TO A JURY TRIAL AND THE RIGHT TO PARTICIPATE IN A CLASS ACTION, CLASS ARBITRATION, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person's claims and may not otherwise preside over any form of a representative or class proceeding.</p>
      <p>You may opt out of this arbitration agreement by emailing <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> within 30 days of first accepting these Terms, stating your name and that you wish to opt out of arbitration. If any part of this section is found unenforceable, the remainder will still apply, except that if the class-action-waiver sentence above is found unenforceable, this entire arbitration section will not apply to that dispute.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Changes to These Terms</h2>
      <p>We may update these terms from time to time. Continued use of RIDAZ after changes become effective means you accept the updated terms.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Contact</h2>
      <p>For questions about these terms, contact <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a>.</p>
    </section>
  </div>
);

const CustomerSupportContent = () => (
  <div className="space-y-6 text-sm leading-7 text-gray-300">
    <p>
      RIDAZ customer support can help with account issues, bug reports, login trouble, content concerns, privacy questions, and general app support.
    </p>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Contact Support</h2>
      <p>Email <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> and include your username, device, screenshots, and the steps that led to the issue when possible.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Support Topics</h2>
      <p>Use customer support for sign-in problems, broken features, profile updates, moderation concerns, account deletion requests, privacy requests, or anything else preventing normal use of RIDAZ.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Faster Help</h2>
      <p>To help us investigate quickly, describe what happened, what you expected to happen, when the issue started, and whether it affects one part of RIDAZ or the whole app.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Privacy and Account Requests</h2>
      <p>If your request involves account ownership, removal, or data access, send the email from the address associated with your RIDAZ account when possible.</p>
    </section>
  </div>
);

const MarketingContent = () => (
  <div className="space-y-6 text-sm leading-7 text-gray-300">
    <p>
      RIDAZ is the social platform built for the action sports community — skaters, BMXers, snowboarders, rollerbladers, scooter riders, and every athlete in between. We exist to connect riders, discover spots, and celebrate the culture.
    </p>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Our Mission</h2>
      <p>To give every action sports athlete a home — a place to share their riding, find local spots, connect with their community, and grow their presence without needing a massive following to get noticed.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Brand Collaborations</h2>
      <p>RIDAZ partners with brands across the action sports space — gear companies, apparel labels, skate shops, event organizers, and media outlets. If you represent a brand that wants to reach an authentic, engaged riding community, reach out.</p>
      <p>Contact us at <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> with the subject line <span className="text-lime-300 font-semibold">Brand Partnership</span> to start a conversation.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Ambassador Program</h2>
      <p>We work with riders who genuinely represent the culture. RIDAZ ambassadors get early access to new features, recognition across the platform, and the opportunity to shape where RIDAZ goes next.</p>
      <p>If you're an active rider with a real community around you, we'd love to hear from you. Email <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> with the subject line <span className="text-lime-300 font-semibold">Ambassador</span> and tell us about yourself and your riding.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Get Featured</h2>
      <p>RIDAZ regularly highlights riders, spots, Zips, and posts from across the community. There's no algorithm to game — just post your best content, tag your sport, and be part of the community. The best content gets seen.</p>
      <p>If you have a specific clip, event, or project you'd like us to know about, reach out at <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> with the subject line <span className="text-lime-300 font-semibold">Feature Request</span>.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Events & Competitions</h2>
      <p>We support local and regional action sports events. If you're organizing a jam, contest, or community session and want RIDAZ involved — whether as a media partner, platform sponsor, or just to help spread the word — get in touch.</p>
      <p>Email <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> with the subject line <span className="text-lime-300 font-semibold">Event Partnership</span>.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Press & Media</h2>
      <p>For press inquiries, interviews, media kits, or coverage of RIDAZ, contact <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a> with the subject line <span className="text-lime-300 font-semibold">Press</span>. We're happy to provide assets, background, and access to the team.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Follow RIDAZ</h2>
      <p>Stay up to date with RIDAZ drops, featured riders, and community highlights across our social channels. Search <span className="text-lime-300 font-semibold">@ridazapp</span> on Instagram and TikTok.</p>
    </section>
  </div>
);

const EulaContent = () => (
  <div className="space-y-6 text-sm leading-7 text-gray-300">
    <p>
      This End User License Agreement ("EULA") is a binding agreement between you and RIDAZ governing your use of the RIDAZ app. By checking the box to agree during sign-up, you accept this EULA in addition to the RIDAZ Terms of Service and Privacy Policy.
    </p>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">License Grant</h2>
      <p>RIDAZ grants you a limited, non-exclusive, non-transferable, revocable license to install and use the app on devices you own or control, solely for your personal, non-commercial use, and subject to this EULA.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Zero Tolerance for Objectionable Content and Abusive Behavior</h2>
      <p>RIDAZ has zero tolerance for objectionable content and abusive users. This includes, without limitation, harassment, hate speech, threats, bullying, sexually explicit material, content that endangers minors, illegal activity, and any content or behavior intended to harm, degrade, or abuse another person.</p>
      <p>RIDAZ uses a combination of automated filtering and user reports to identify objectionable content. Users can flag any post, comment, or profile they believe violates this policy, and can block any user directly from the app, which immediately removes that user's content from their feed and notifies RIDAZ for review.</p>
      <p>RIDAZ will review reports of objectionable content and will act within 24 hours of a report, up to and including removing the content and permanently ejecting the offending user from the platform.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Your Responsibilities</h2>
      <p>You agree not to upload, post, or transmit objectionable content as described above, and not to use RIDAZ to harass, abuse, impersonate, or endanger any other person. Violating this EULA may result in immediate content removal, account suspension, or permanent termination of your account without notice.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Ownership</h2>
      <p>RIDAZ and its licensors retain all right, title, and interest in and to the app itself, including all intellectual property rights. This EULA does not grant you any rights to RIDAZ's trademarks, branding, or underlying source code.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Termination</h2>
      <p>This license is effective until terminated. RIDAZ may terminate this EULA and your access to the app at any time, with or without notice, if you fail to comply with any term of this EULA, the Terms of Service, or applicable law. Upon termination, you must stop using the app.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Disclaimer and Liability</h2>
      <p>The app is provided "as is" without warranties of any kind to the fullest extent permitted by law. To the fullest extent permitted by law, RIDAZ is not liable for indirect, incidental, or consequential damages arising from your use of the app.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Changes to This EULA</h2>
      <p>RIDAZ may update this EULA from time to time. Continued use of the app after changes become effective means you accept the updated EULA.</p>
    </section>

    <section>
      <h2 className="text-white text-lg font-bold mb-2">Contact</h2>
      <p>For questions about this EULA, contact <a className="text-lime-300 underline" href="mailto:nikita@ridaz.app">nikita@ridaz.app</a>.</p>
    </section>
  </div>
);

const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ kind, onClose }) => {
  const isPrivacy = kind === 'privacy';
  const isTerms = kind === 'terms';
  const isMarketing = kind === 'marketing';
  const isEula = kind === 'eula';
  const title = isPrivacy ? 'Privacy Policy' : isTerms ? 'Terms of Service' : isMarketing ? 'Marketing & Partnerships' : isEula ? 'EULA' : 'Customer Support';

  return (
    <div
      className="min-h-screen overflow-y-auto bg-app-backdrop text-white"
      style={{ height: 'min(100dvh, var(--app-height, 100vh))', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}
    >
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-white/10 bg-black/70 px-4 pb-3 backdrop-blur-md" style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}>
        <button onClick={onClose} className="rounded-full p-2 text-white hover:bg-white/10">
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <div>
          <h1 className="text-lg font-black">{title}</h1>
          <p className="text-xs uppercase tracking-[0.16em] text-gray-400">Effective {effectiveDate}</p>
        </div>
      </header>

      <div className="mx-auto w-full max-w-[820px] px-4 py-6 pb-24">
        <div className="rounded-3xl border border-white/10 bg-black/40 p-5 shadow-2xl shadow-black/30">
          {isPrivacy ? <PrivacyContent /> : isTerms ? <TermsContent /> : isMarketing ? <MarketingContent /> : isEula ? <EulaContent /> : <CustomerSupportContent />}
        </div>
      </div>
    </div>
  );
};

export default LegalDocumentPage;
